import { useQuery } from '@apollo/client';
import { FormProvider, useWatch, yupResolver } from '@slal/ui/react-hook-form/core';
import { Field, FieldSet, RadioFieldGroup } from '@slal/ui/react-hook-form/fields';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { MaxWidthContent, StyledForm } from '~/components';
import { InvestmentPathwayBreakdown, InvestmentPathwayContradictoryWarning } from '~/features';
import { ListInvestmentPathways } from '~/graphql';
import { useApplicationForm } from '~/hooks';
import { investmentPathwayOptionsValidationSchema } from '~/schemas';
import { ApplicationFormKeys, BaseFund, InvestmentPathwayOptionSchema } from '~/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { options } from './utils';

export const InvestmentPathwayOptionsForm = () => {
    const { data } = useQuery(ListInvestmentPathways);
    const [hasChargeError, setHasChargeError] = useState(true);

    const { handleSubmit, form } = useApplicationForm<InvestmentPathwayOptionSchema>(
        {
            resolver: yupResolver(investmentPathwayOptionsValidationSchema),
        },
        ApplicationFormKeys.INVESTMENT_PATHWAY_OPTIONS
    );

    const navigate = useNavigate();
    const onValidCallback = () => navigate('/application/key-considerations/information');

    const investmentPathwayFundCode = useWatch({
        control: form.control,
        name: 'investmentPathway.fundCode',
    });

    useEffect(() => {
        if (!investmentPathwayFundCode || !data) return;
        const investmentPathway: BaseFund = data?.listInvestmentPathways?.find(
            ({ fundCode }: BaseFund) => fundCode === investmentPathwayFundCode
        );
        if (!investmentPathway?.fundName) return;
        form.setValue('investmentPathway.fundName', investmentPathway.fundName as never);
    }, [data, form, investmentPathwayFundCode]);

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <FieldSet aria-label="Investment pathway options">
                        <Field
                            label="Which statement best describes your plan for your remaining pension pot?"
                            errorFor="investmentPathway.fundCode"
                        >
                            <RadioFieldGroup name="investmentPathway.fundCode" options={options} stacked />
                        </Field>
                    </FieldSet>
                    <InvestmentPathwayContradictoryWarning fundCode={investmentPathwayFundCode} />
                    <InvestmentPathwayBreakdown
                        fundCode={investmentPathwayFundCode}
                        onErrorChange={setHasChargeError}
                    />

                    <ApplicationButtons backTo="/application/investment-options" hasError={hasChargeError} />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
